import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Loader2,
  Package,
  Wrench,
} from 'lucide-react';
import { EQUIPEMENT_CRITICITES, EQUIPEMENT_STATUTS, EQUIPEMENT_TYPES } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import {
  EMPTY_REPORTING_FILTERS,
  ReportingFilters,
  type ReportingFilterValues,
} from '../../components/reporting/ReportingFilters';
import { ExportButtons } from '../../components/reporting/ExportButtons';
import { defaultDateRange, toIsoFromDate, toIsoToDate } from '../../components/reporting/dateRange';
import {
  CRITICITE_LABEL,
  STATUT_EQUIPEMENT_LABEL,
  TYPE_EQUIPEMENT_LABEL,
  formatCurrency,
  formatDate,
  formatNumber,
} from '../../components/reporting/format';
import type { EquipementsReportResult } from '../../components/reporting/types';

/**
 * Page `/reporting/equipements` (US-REP-02).
 *
 * - Filtres : service, bâtiment, type, statut, criticité, dates.
 * - Cards résumé par type / statut / criticité + totaux.
 * - Table inventaire complète (code, désignation, type, statut, criticité,
 *   service, bâtiment/salle, âge, interventions 12 mois, coût 12 mois,
 *   conformité).
 * - Boutons Excel + PDF.
 */
interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}

interface BatimentOption {
  id: string;
  code: string;
  nom: string;
}

export function EquipementsReportPage() {
  const def = defaultDateRange();
  const [filters, setFilters] = useState<ReportingFilterValues>({
    ...EMPTY_REPORTING_FILTERS,
    date_from: def.from,
    date_to: def.to,
  });
  const [data, setData] = useState<EquipementsReportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [batiments, setBatiments] = useState<BatimentOption[]>([]);

  const printRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
    api
      .get<{ data: BatimentOption[] }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => setBatiments([]));
  }, []);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    const fromIso = toIsoFromDate(filters.date_from);
    const toIso = toIsoToDate(filters.date_to);
    if (fromIso) p.set('date_from', fromIso);
    if (toIso) p.set('date_to', toIso);
    if (filters.service_id) p.set('service_id', filters.service_id);
    if (filters.batiment_id) p.set('batiment_id', filters.batiment_id);
    if (filters.type) p.set('type', filters.type);
    if (filters.statut) p.set('statut', filters.statut);
    if (filters.criticite) p.set('criticite', filters.criticite);
    return p;
  }, [filters]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.toString()]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const qs = queryParams.toString();
      const res = await api.get<{ data: EquipementsReportResult }>(
        `/reporting/equipements${qs ? `?${qs}` : ''}`,
      );
      setData(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Wrench className="w-7 h-7 mr-2 text-primary-600" />
            Rapport Équipements
          </h1>
          <p className="text-sm text-gray-500 mt-1">Inventaire détaillé, coûts et conformité.</p>
        </div>
        <ExportButtons
          excelUrl="/reporting/equipements.xlsx"
          excelParams={queryParams}
          filename="rapport-equipements"
          targetRef={printRef}
          pdfDisabled={!data}
        />
      </div>

      <ReportingFilters
        values={filters}
        onChange={setFilters}
        onRefresh={refresh}
        fields={{
          dates: true,
          service: true,
          batiment: true,
          type: true,
          statut: true,
          criticite: true,
        }}
        servicesOptions={services.map((s) => ({
          value: s.id,
          label: `${s.code} — ${s.nom_complet}`,
        }))}
        batimentsOptions={batiments.map((b) => ({
          value: b.id,
          label: `${b.code} — ${b.nom}`,
        }))}
        typeOptions={EQUIPEMENT_TYPES.map((t) => ({
          value: t,
          label: TYPE_EQUIPEMENT_LABEL[t] ?? t,
        }))}
        statutOptions={EQUIPEMENT_STATUTS.map((s) => ({
          value: s,
          label: STATUT_EQUIPEMENT_LABEL[s] ?? s,
        }))}
        criticiteOptions={EQUIPEMENT_CRITICITES.map((c) => ({
          value: c,
          label: CRITICITE_LABEL[c] ?? c,
        }))}
      />

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center p-24 bg-white rounded-lg shadow-sm border border-gray-200">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
        </div>
      ) : data ? (
        <div ref={printRef} className="space-y-4">
          {/* Synthèse */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              label="Total équipements"
              value={formatNumber(data.resume.total)}
              icon={Package}
              tone="primary"
            />
            <SummaryCard
              label="Interventions 12 mois"
              value={formatNumber(data.totaux.nb_interventions_total)}
              icon={Wrench}
              tone="blue"
            />
            <SummaryCard
              label="Coût maintenance 12 mois"
              value={formatCurrency(data.totaux.cout_maintenance_total)}
              icon={Wrench}
              tone="orange"
            />
            <SummaryCard
              label="Équipements avec alertes"
              value={formatNumber(data.totaux.nb_equipements_avec_alertes)}
              icon={AlertTriangle}
              tone="red"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel title="Par type">
              <DistList
                items={data.resume.par_type.map((p) => ({
                  key: p.type,
                  label: TYPE_EQUIPEMENT_LABEL[p.type] ?? p.type,
                  count: p.count,
                }))}
              />
            </Panel>
            <Panel title="Par statut">
              <DistList
                items={data.resume.par_statut.map((p) => ({
                  key: p.statut,
                  label: STATUT_EQUIPEMENT_LABEL[p.statut] ?? p.statut,
                  count: p.count,
                }))}
              />
            </Panel>
            <Panel title="Par criticité">
              <DistList
                items={data.resume.par_criticite.map((p) => ({
                  key: p.criticite,
                  label: CRITICITE_LABEL[p.criticite] ?? p.criticite,
                  count: p.count,
                }))}
              />
            </Panel>
          </div>

          {/* Inventaire */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                Inventaire ({formatNumber(data.inventaire.length)})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Code</Th>
                    <Th>Désignation</Th>
                    <Th>Type</Th>
                    <Th>Statut</Th>
                    <Th>Criticité</Th>
                    <Th>Service</Th>
                    <Th>Bâtiment / Salle</Th>
                    <Th right>Âge (j)</Th>
                    <Th right>Interv. 12m</Th>
                    <Th right>Coût 12m</Th>
                    <Th>Conformité</Th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {data.inventaire.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-500">
                        Aucun équipement pour ces filtres.
                      </td>
                    </tr>
                  ) : (
                    data.inventaire.map((eq) => (
                      <tr key={eq.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-primary-600 whitespace-nowrap">
                          {eq.code}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 max-w-[240px] truncate"
                          title={eq.designation}
                        >
                          {eq.designation}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {TYPE_EQUIPEMENT_LABEL[eq.type] ?? eq.type}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {STATUT_EQUIPEMENT_LABEL[eq.statut] ?? eq.statut}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {eq.criticite ? (CRITICITE_LABEL[eq.criticite] ?? eq.criticite) : '—'}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-700 max-w-[180px] truncate"
                          title={eq.service_nom ?? ''}
                        >
                          {eq.service_nom ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {eq.batiment_code ?? '—'}
                          {eq.salle_code ? ` / ${eq.salle_code}` : ''}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 tabular-nums">
                          {eq.age_jours !== null ? formatNumber(eq.age_jours) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 tabular-nums">
                          {formatNumber(eq.nb_interventions_12_mois)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 tabular-nums">
                          {formatCurrency(eq.cout_maintenance_12_mois)}
                        </td>
                        <td className="px-3 py-2">
                          <ConformiteBadge value={eq.conformite} />
                          {eq.prochain_etalonnage && (
                            <span className="ml-1 text-[10px] text-gray-400">
                              ({formatDate(eq.prochain_etalonnage.date)})
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[10px] text-gray-400 text-right pt-2">
            Généré le {new Date().toLocaleString('fr-FR')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string;
  icon: typeof Package;
  tone: 'primary' | 'blue' | 'orange' | 'red';
}) {
  const TONE: Record<typeof tone, string> = {
    primary: 'text-primary-600',
    blue: 'text-blue-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${TONE[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${TONE[tone]}`}>{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function DistList({ items }: { items: Array<{ key: string; label: string; count: number }> }) {
  const total = items.reduce((acc, it) => acc + it.count, 0);
  if (items.length === 0 || total === 0) {
    return <p className="text-xs text-gray-500 py-4 text-center">Aucune donnée.</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it) => {
        const pct = total > 0 ? (it.count / total) * 100 : 0;
        return (
          <li key={it.key}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-medium text-gray-700 truncate pr-2">{it.label}</span>
              <span className="text-gray-600 tabular-nums">
                {formatNumber(it.count)} · {pct.toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-primary-500 rounded-full" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ConformiteBadge({ value }: { value: 'conforme' | 'non_conforme' | 'a_verifier' }) {
  if (value === 'conforme') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="w-3 h-3 mr-0.5" />
        Conforme
      </span>
    );
  }
  if (value === 'non_conforme') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700">
        <AlertTriangle className="w-3 h-3 mr-0.5" />
        Non conforme
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
      <HelpCircle className="w-3 h-3 mr-0.5" />À vérifier
    </span>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
